import { useCallback, useEffect, useMemo, useState } from "react";

import { addYears } from "date-fns";
import {
  ArrowLeft,
  CalendarDays,
  ChevronLeftIcon,
  ChevronRightIcon,
  XIcon,
} from "lucide-react";
import { useForm } from "react-hook-form";

import { DateTimeInputV2 } from "@/components/pieces/DateTimeInputV2";
import { DifficultyTag } from "@/components/pieces/roleplays/DifficultyTag";
import { PeopleAndGroupInput } from "@/components/pieces/roleplays/PeopleAndGroupInput";
import { Scenario } from "@/components/pieces/roleplays/ScenarioCard";
import { AvatarWithPrettyNull } from "@/components/pieces/users/AvatarWithPrettyNull";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { SharingStatusIcon } from "@/components/ui/sharing/SharingStatusIcon";
import { Textarea } from "@/components/ui/textarea";
import { SUBTEXT_COLOR } from "@/css/constants";
import { ScenarioDifficulty, SharingStatus } from "@/graphql";
import { getBrowserTimezone } from "@/utils/datetime";

interface AssignScenarioDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit?: (data: AssignScenarioFormData) => void;
  collectionId?: string; // New prop to determine if scenario selection is needed
}

interface Assignment {
  id: string;
  name: string;
  email: string;
  dueDate?: string;
  status?: "due" | "past-due" | "no-due";
}

interface SelectedTag {
  id: string;
  name: string;
  type: "person" | "group";
}

interface AssignScenarioFormData {
  selectedTags: SelectedTag[];
  searchValue: string;
  setDueDate: boolean;
  dueDate?: Date;
  timezone: string;
  includeCustomMessage: boolean;
  customMessage: string;
  selectedScenarios?: string[]; // New field for scenario selection
}

// Mock data for current assignments
const mockAssignments: Assignment[] = [
  {
    id: "1",
    name: "Alicia Linehan",
    email: "alicia@exec.com",
    dueDate: "Oct 2, 2025",
    status: "due",
  },
  {
    id: "2",
    name: "Emily Yates",
    email: "emily@exec.com",
    dueDate: "Sep 15, 2025",
    status: "past-due",
  },
  {
    id: "3",
    name: "Brendan Rogers",
    email: "brendan@exec.com",
    status: "no-due",
  },
  {
    id: "4",
    name: "Melanie Swanson",
    email: "melanie@exec.com",
    status: "no-due",
  },
];

// Mock data for scenarios
const mockScenarios: Scenario[] = [
  {
    id: "1",
    name: "Finding Common Ground",
    slug: "finding-common-ground",
    difficulty: ScenarioDifficulty.Easy,
    sharingStatus: SharingStatus.Workspace,
  },
  {
    id: "2",
    name: "Managing Team Dynamics",
    slug: "managing-team-dynamics",
    difficulty: ScenarioDifficulty.Easy,
    sharingStatus: SharingStatus.Private,
  },
  {
    id: "3",
    name: "Handling Difficult Conversations",
    slug: "handling-difficult-conversations",
    difficulty: ScenarioDifficulty.Medium,
    sharingStatus: SharingStatus.Workspace,
  },
  {
    id: "4",
    name: "Negotiating Win-Win Solutions",
    slug: "negotiating-win-win-solutions",
    difficulty: ScenarioDifficulty.Hard,
    sharingStatus: SharingStatus.Global,
  },
];

function AssignmentCard({ assignment }: { assignment: Assignment }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-full bg-gray-200 flex-shrink-0" />
        <div className="flex flex-col">
          <span className="font-semibold text-sm text-gray-900">
            {assignment.name}
          </span>
          <span className="text-sm text-gray-500">{assignment.email}</span>
        </div>
      </div>
      <div className="text-right">
        {assignment.status === "due" && assignment.dueDate && (
          <span className="text-sm text-gray-500">
            Due {assignment.dueDate}
          </span>
        )}
        {assignment.status === "past-due" && assignment.dueDate && (
          <div className="flex items-center gap-1">
            <div className="w-3.5 h-3.5 rounded-full bg-amber-500 flex items-center justify-center">
              <XIcon className="w-2 h-2 text-white" />
            </div>
            <span className="text-sm text-amber-600">
              Past Due {assignment.dueDate}
            </span>
          </div>
        )}
        {assignment.status === "no-due" && (
          <span className="text-sm text-gray-400">No Due Date</span>
        )}
      </div>
    </div>
  );
}

export function SimpleScenarioCard({
  scenario,
  selected,
  onToggle,
  selectable = true,
}: {
  scenario: Scenario;
  selected: boolean;
  onToggle: () => void;
  selectable?: boolean;
}) {
  // Simplified - just use onToggle directly without complex callbacks
  const handleCardClick = (e: React.MouseEvent) => {
    // Prevent double firing if clicking on checkbox
    if ((e.target as HTMLElement).closest('[role="checkbox"]')) {
      return;
    }
    onToggle();
  };

  return (
    <div
      className={`flex gap-3 p-3 border bg-white rounded-lg cursor-pointer transition-colors ${
        selected
          ? "border-blue-500 bg-blue-50"
          : "border-gray-200 hover:border-gray-300"
      }`}
      onClick={handleCardClick}
    >
      {selectable && (
        <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
          <Checkbox checked={selected} onCheckedChange={onToggle} />
        </div>
      )}

      <AvatarWithPrettyNull
        user={{
          name: scenario.personaName,
          photo: scenario.personaPhotoUrl,
        }}
        size={60}
        data-test-id="scenario-image"
      />

      <div className="flex-1 min-w-0">
        <div className="flex items-center mb-2 justify-between">
          <div className="font-semibold text-sm text-gray-900">
            {scenario.name}
          </div>
          <div className="flex items-center justify-center h-6 w-6">
            {scenario.sharingStatus && (
              <SharingStatusIcon
                sharingStatus={scenario.sharingStatus}
                size={14}
              />
            )}
          </div>
        </div>

        <DifficultyTag difficulty={scenario.difficulty} />
      </div>
    </div>
  );
}

enum Step {
  SELECT_PEOPLE_AND_GROUPS = 0,
  CONFIG_ASSIGNMENT = 1,
  CHOOSE_SCENARIO = 2,
}

export function AssignScenarioDialog({
  open,
  onOpenChange,
  onSubmit,
  collectionId, // New prop
}: AssignScenarioDialogProps) {
  const [step, setStep] = useState(Step.SELECT_PEOPLE_AND_GROUPS);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedScenarios, setSelectedScenarios] = useState<string[]>([]);
  const { register, handleSubmit, watch, setValue, reset } =
    useForm<AssignScenarioFormData>({
      defaultValues: {
        selectedTags: [],
        searchValue: "",
        setDueDate: true,
        dueDate: undefined,
        timezone: "PDT",
        includeCustomMessage: true,
        customMessage: "",
        selectedScenarios: [],
      },
    });

  const selectedTags = watch("selectedTags");
  const setDueDate = watch("setDueDate");
  const includeCustomMessage = watch("includeCustomMessage");
  const dueDate = watch("dueDate");
  const isConfigAssignment = step === Step.CONFIG_ASSIGNMENT;
  const isChooseScenario = step === Step.CHOOSE_SCENARIO;
  const [timezone, setTimezone] = useState(getBrowserTimezone());
  const maxDate = addYears(new Date(), 1);
  const minDate = new Date();

  // Pagination for scenarios
  const scenariosPerPage = 4;
  const totalScenarios = mockScenarios.length;
  const totalPages = Math.ceil(totalScenarios / scenariosPerPage);
  const startIndex = (currentPage - 1) * scenariosPerPage;
  const endIndex = startIndex + scenariosPerPage;
  const currentScenarios = mockScenarios.slice(startIndex, endIndex);

  useEffect(() => {
    if (selectedTags.length > 0) {
      setStep(Step.CONFIG_ASSIGNMENT);
    }
  }, [selectedTags]);

  const handleFormSubmit = (data: AssignScenarioFormData) => {
    const submitData = {
      ...data,
      selectedScenarios: collectionId ? selectedScenarios : undefined,
    };
    onSubmit?.(submitData);
    onOpenChange(false);
  };

  const handleClose = () => {
    reset();
    setSelectedScenarios([]);
    setStep(Step.SELECT_PEOPLE_AND_GROUPS);
    setCurrentPage(1);
    onOpenChange(false);
  };

  const handleNextFromAssignment = () => {
    if (collectionId) {
      setStep(Step.CHOOSE_SCENARIO);
    } else {
      handleSubmit(handleFormSubmit)();
    }
  };

  const toggleScenario = useCallback((scenarioId: string) => {
    setSelectedScenarios((prev) =>
      prev.includes(scenarioId)
        ? prev.filter((id) => id !== scenarioId)
        : [...prev, scenarioId],
    );
  }, []);

  // Create stable callbacks for each scenario to prevent re-renders
  const scenarioCallbacks = useMemo(() => {
    const callbacks: Record<string, () => void> = {};
    currentScenarios.forEach((scenario) => {
      callbacks[scenario.id] = () => toggleScenario(scenario.id);
    });
    return callbacks;
  }, [currentScenarios, toggleScenario]);

  const getDialogTitle = () => {
    if (isChooseScenario) return "Choose Scenarios";
    return "Assign Scenario";
  };

  const getSubmitButtonText = () => {
    if (isChooseScenario) {
      const count = selectedScenarios.length;
      return count > 0
        ? `Assign ${count} Scenario${count > 1 ? "s" : ""}`
        : "Assign";
    }
    return collectionId ? "Next" : "Assign";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[550px] p-0 pb-4" closeClassName="top-5">
        <DialogHeader className="px-4 py-4 border-b text-left justify-center">
          <div className="flex items-center gap-2">
            {(step === Step.CONFIG_ASSIGNMENT ||
              step === Step.CHOOSE_SCENARIO) && (
              <ArrowLeft
                className="w-4 h-4 cursor-pointer"
                onClick={() => {
                  if (step === Step.CHOOSE_SCENARIO) {
                    setStep(Step.CONFIG_ASSIGNMENT);
                  } else {
                    setValue("selectedTags", []);
                    setStep(Step.SELECT_PEOPLE_AND_GROUPS);
                  }
                }}
              />
            )}
            <div className="text-xl font-semibold">{getDialogTitle()}</div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <div className="px-4">
            {/* Scenario Selection Step */}
            {isChooseScenario && (
              <div className="space-y-4">
                <div className="space-y-3">
                  {currentScenarios.map((scenario) => (
                    <SimpleScenarioCard
                      key={scenario.id}
                      scenario={scenario}
                      selected={selectedScenarios.includes(scenario.id)}
                      onToggle={scenarioCallbacks[scenario.id]}
                    />
                  ))}
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between pt-4">
                  <div className="text-sm text-gray-500">
                    {startIndex + 1}-{Math.min(endIndex, totalScenarios)} of{" "}
                    {totalScenarios}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(1, prev - 1))
                      }
                      disabled={currentPage === 1}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronLeftIcon className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                      }
                      disabled={currentPage === totalPages}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronRightIcon className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Action Buttons for Scenario Selection */}
                <div className="flex justify-end gap-4 pt-6">
                  <Button
                    variant="minimal"
                    onClick={handleClose}
                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-[40px]"
                    type="button"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 h-[40px]"
                    disabled={selectedScenarios.length === 0}
                  >
                    {getSubmitButtonText()}
                  </Button>
                </div>
              </div>
            )}

            {/* People & Groups Selection - Only show in first two steps */}
            {!isChooseScenario && (
              <div className="space-y-2">
                <Label className="text-base font-semibold">
                  People & Groups
                </Label>

                <PeopleAndGroupInput
                  onChange={(users, groups) => {
                    const userTags = users.map((user) => ({
                      id: user.id ?? "",
                      name: user.name ?? "",
                      type: "person" as const,
                    }));

                    const groupTags = groups.map((group) => ({
                      id: group.id ?? "",
                      name: group.name ?? "",
                      type: "group" as const,
                    }));

                    setValue("selectedTags", [...userTags, ...groupTags]);
                  }}
                />
              </div>
            )}

            {/* Current Assignments - Only show when no selections */}
            {!isConfigAssignment && !isChooseScenario && (
              <div className="bg-gray-50 rounded-md p-0">
                <div className="flex items-center justify-between pt-6 pb-2">
                  <div className="font-semibold text-base">
                    Current Assignments (54)
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-gray-600 border-gray-200 bg-gray-100 h-[40px]"
                    type="button"
                  >
                    See All
                  </Button>
                </div>
                <div className="pb-4">
                  {mockAssignments.map((assignment) => (
                    <AssignmentCard
                      key={assignment.id}
                      assignment={assignment}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Due Date Section - Only show when has selections and not choosing scenarios */}
            {isConfigAssignment && !isChooseScenario && (
              <div className="space-y-4 pt-6">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    checked={setDueDate}
                    onCheckedChange={(checked) =>
                      setValue("setDueDate", checked === true)
                    }
                  />
                  <Label className="text-sm text-black font-normal">
                    Set a due date for this assignment
                  </Label>
                </div>

                {setDueDate && (
                  <div className="space-y-4">
                    <Label className="text-base font-semibold mb-1">
                      Due Date
                    </Label>
                    <div className="flex gap-3 w-full">
                      <div style={{ position: "relative", width: "100%" }}>
                        <CalendarDays
                          style={{
                            position: "absolute",
                            top: 12,
                            left: 10,
                            zIndex: 99,
                            color: SUBTEXT_COLOR,
                          }}
                          strokeWidth={1.5}
                        />
                        <DateTimeInputV2
                          onChange={(date) => {
                            setValue(
                              "dueDate",
                              date ? new Date(date) : undefined,
                            );
                          }}
                          value={dueDate?.toISOString() ?? null}
                          timezone={timezone}
                          onChangeTimezone={setTimezone}
                          maxDateTime={maxDate}
                          minDateTime={minDate}
                          containerStyles={{
                            height: "50px",
                            width: "100%",
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Custom Message Section - Only show when has selections and not choosing scenarios */}
            {isConfigAssignment && !isChooseScenario && (
              <div className="space-y-4 pt-6">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    checked={includeCustomMessage}
                    onCheckedChange={(checked) =>
                      setValue("includeCustomMessage", checked === true)
                    }
                  />
                  <Label className="text-sm text-gray-700 font-normal">
                    Include custom message
                  </Label>
                </div>

                {includeCustomMessage && (
                  <Textarea
                    {...register("customMessage")}
                    placeholder="Message..."
                    className="min-h-[96px] text-sm"
                  />
                )}
              </div>
            )}

            {/* Action Buttons - Only show when has selections and not choosing scenarios */}
            {isConfigAssignment && !isChooseScenario && (
              <div className="flex justify-end gap-4 pt-6">
                <Button
                  variant="minimal"
                  onClick={handleClose}
                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-[40px]"
                  type="button"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleNextFromAssignment}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 h-[40px]"
                  type="button"
                >
                  {getSubmitButtonText()}
                </Button>
              </div>
            )}

            {/* Done Button - Only show when no selections and not choosing scenarios */}
            {!isConfigAssignment && !isChooseScenario && (
              <div className="flex justify-end">
                <Button
                  onClick={handleClose}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 h-[40px]"
                  type="button"
                >
                  Done
                </Button>
              </div>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
